import { Languages } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { TargetLocalesSelect } from '~/components/common/target-locales-select';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { useCanTranslateContent } from '~/hooks/useCanTranslateContent';
import { cn } from '~/utils/cn';
import { formatDateTime } from '~/utils/date';
import { extractApiError } from '~/utils/extract-api-error';

import { useRunTranslations, useTranslateRun } from '../../hooks/use-translations';

interface BatchTranslationsDialogProps {
  /** Any id of the run — job, pipeline or sample; workflow-service resolves the scope. */
  workflowId: string;
  /** Locales the run was configured to deliver (`runConfig.targetLocales`), if any. */
  configuredLocales?: string[] | undefined;
  className?: string;
}

/**
 * The run's languages: which locales it has been translated into (with QA counts), and a way to
 * translate its enriched English into more — no re-enrichment. Internal users only, Google runs
 * only (the host decides the second).
 */
export function BatchTranslationsDialog({
  workflowId,
  configuredLocales,
  className,
}: BatchTranslationsDialogProps) {
  const canTranslate = useCanTranslateContent();
  const [open, setOpen] = useState(false);
  const [locales, setLocales] = useState<string[]>([]);
  const summary = useRunTranslations(workflowId, { enabled: canTranslate && open, poll: open });
  const translate = useTranslateRun(workflowId);
  if (!canTranslate) return null;

  const rows = summary.data ?? [];
  const onTranslate = () => {
    translate.mutate(
      { locales },
      {
        onSuccess: (res) => {
          toast.success(
            `Translating ${String(res.productCount)} product(s) into ${res.locales.join(', ')} — rows appear below as they land.`,
          );
          setLocales([]);
        },
        onError: (error) => {
          toast.error('Could not start the translation', {
            description: extractApiError(error, 'Try again in a moment.'),
          });
        },
      },
    );
  };

  const label = 'Languages';
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>
          <DialogTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              aria-label={label}
              className={cn('shrink-0 cursor-pointer', className)}
              data-testid="translations-open-dialog-button"
            >
              <Languages className="h-4 w-4" />
            </Button>
          </DialogTrigger>
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
      <DialogContent className="sm:max-w-xl" data-testid="translations-dialog">
        <DialogHeader>
          <DialogTitle data-testid="translations-dialog-title">Languages</DialogTitle>
          <DialogDescription>
            English (en-US) is the enriched source. Other locales are translated from it and stored
            separately; re-translating a locale replaces it.
          </DialogDescription>
        </DialogHeader>

        {configuredLocales && configuredLocales.length > 0 && (
          <div
            className="flex flex-wrap items-center gap-1.5 text-xs"
            data-testid="translations-configured-locales-list"
          >
            <span className="text-muted-foreground">Configured for this run:</span>
            {configuredLocales.map((l) => (
              <Badge
                key={l}
                variant="outline"
                className="rounded-md px-1.5 py-0.5 text-[11px]"
                data-testid={`translations-configured-locale-badge-${l}`}
              >
                {l}
              </Badge>
            ))}
          </div>
        )}

        <div className="rounded-lg border">
          {summary.isPending && (
            <p
              className="text-muted-foreground px-3 py-4 text-sm"
              data-testid="translations-loading"
            >
              Loading…
            </p>
          )}
          {summary.isError && (
            <p className="text-destructive px-3 py-4 text-sm" data-testid="translations-error">
              Could not load this run's languages.
            </p>
          )}
          {summary.isSuccess && rows.length === 0 && (
            <p className="text-muted-foreground px-3 py-4 text-sm" data-testid="translations-empty">
              Not translated yet.
            </p>
          )}
          {rows.length > 0 && (
            <table className="w-full text-sm" data-testid="translations-table">
              <thead className="text-muted-foreground text-xs">
                <tr className="border-b">
                  <th className="px-3 py-2 text-left font-medium">Locale</th>
                  <th className="px-3 py-2 text-right font-medium">Products</th>
                  <th className="px-3 py-2 text-right font-medium">Pass</th>
                  <th className="px-3 py-2 text-right font-medium">Flag</th>
                  <th className="px-3 py-2 text-right font-medium">Held back</th>
                  <th className="px-3 py-2 text-right font-medium">Updated</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={`${r.locale}-${r.enrichmentPhase ?? ''}`}
                    className="border-b last:border-0"
                    data-testid={`translations-row-${r.locale}${r.enrichmentPhase === 'SAMPLE' ? '-sample' : ''}`}
                  >
                    <td className="px-3 py-2">
                      {r.locale}
                      {r.enrichmentPhase === 'SAMPLE' && (
                        <span className="text-muted-foreground ml-1 text-xs">(sample)</span>
                      )}
                    </td>
                    <td
                      className="px-3 py-2 text-right tabular-nums"
                      data-testid={`translations-row-${r.locale}${r.enrichmentPhase === 'SAMPLE' ? '-sample' : ''}-products-value`}
                    >
                      {r.productCount}
                    </td>
                    <td
                      className="px-3 py-2 text-right tabular-nums"
                      data-testid={`translations-row-${r.locale}${r.enrichmentPhase === 'SAMPLE' ? '-sample' : ''}-pass-value`}
                    >
                      {r.passCount}
                    </td>
                    <td
                      className="px-3 py-2 text-right tabular-nums"
                      data-testid={`translations-row-${r.locale}${r.enrichmentPhase === 'SAMPLE' ? '-sample' : ''}-flag-value`}
                    >
                      {r.flagCount}
                    </td>
                    <td
                      data-testid={`translations-row-${r.locale}${r.enrichmentPhase === 'SAMPLE' ? '-sample' : ''}-held-back-value`}
                      className={cn(
                        'px-3 py-2 text-right tabular-nums',
                        r.failCount > 0 && 'text-destructive',
                      )}
                    >
                      {r.failCount}
                    </td>
                    <td className="text-muted-foreground px-3 py-2 text-right text-xs">
                      {r.lastUpdatedAt ? formatDateTime(r.lastUpdatedAt) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <TargetLocalesSelect
          idPrefix="batch-translate-locale"
          testId="translations-target-locales"
          value={locales}
          onChange={setLocales}
          disabled={translate.isPending}
        />

        <DialogFooter>
          <Button
            onClick={onTranslate}
            disabled={locales.length === 0 || translate.isPending}
            className="cursor-pointer"
            data-testid="translations-dialog-translate-button"
          >
            {translate.isPending ? 'Starting…' : 'Translate'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
